/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CosmosElementLearnImageCardFragment } from "@cosmos/hygraph-schema";

import {
	CosmosCard,
	CosmosDesc,
	CosmosImage,
	CosmosText,
	CosmosTextVariantEnum,
	CosmosTitle,
	EdsIcon,
} from "@components/elements";
import { toShortNum } from "@components/utils";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { vstack } from "@panda/patterns";

import { useDisabledHygraphImageLoader, useHygraphImageLoader } from "@components/hooks";
import { Source } from "./Source.molecule";

const learnCardStyle = cx(
	vstack({
		direction: "column",
		alignItems: "flex-start",
		justifyContent: "stretch",
		gap: "eds.dimension.16",
	}),
	css({
		w: "full",
		lgTo2xl: {
			h: "full",
		},
	}),
);
const learnCardDescStyle = css({
	lineClamp: "6",
	display: "-webkit-box",
	boxOrient: "vertical",
	overflow: "hidden",
});

const learnCardLinkStyle = css({ width: "100%", textAlign: "start" });
const learnCardTitleStyle = css({
	fontWeight: "bold",
	lineClamp: "3",
	display: "-webkit-box",
	boxOrient: "vertical",
	overflow: "hidden",
});
export type LearnCardProps = CosmosElementLearnImageCardFragment & {
	squareImage?: boolean;
	upVotesCount?: number;
	threadCommentsCount?: number;
};

export function LearnCard({
	img,
	imageLabel,
	source,
	title,
	desc,
	url,
	squareImage,
	upVotesCount,
	threadCommentsCount,
}: LearnCardProps): React.JSX.Element {
	const hygraphImageLoader = useHygraphImageLoader();
	const disabledHygraphImageLoader = useDisabledHygraphImageLoader();
	const optUrl = img?.optUrl;

	return (
		<CosmosCard padding={16} className={cx("learn-card", learnCardStyle)} variant="material">
			{img ? (
				<CosmosCard.Thumbnail aspectRatio={squareImage ? "1/1" : "5/3"}>
					<CosmosImage
						alt={imageLabel || ""}
						className="image"
						src={optUrl || img?.url}
						width={400}
						height={squareImage ? 400 : 225}
						loader={optUrl ? disabledHygraphImageLoader : hygraphImageLoader}
						unoptimized={!!optUrl}
						sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 400px"
					/>
				</CosmosCard.Thumbnail>
			) : (
				""
			)}
			<VStack gap="eds.dimension.2" alignItems="start">
				<CosmosCard.Link href={url || ""} className={learnCardLinkStyle}>
					<CosmosTitle
						title={{ html: title?.html, text: title?.text, variant: CosmosTextVariantEnum.uiMD }}
						className={learnCardTitleStyle}
					/>
				</CosmosCard.Link>
				{source && (source.author || source.timestamp) ? <Source {...source}></Source> : null}
				<CosmosDesc
					description={{
						html: desc,
						variant: CosmosTextVariantEnum.paragraphSM,
						color: "eds.text.secondary",
					}}
					className={learnCardDescStyle}
				/>
			</VStack>
			{!!upVotesCount && !!threadCommentsCount && (
				<HStack gap="eds.dimension.16">
					<HStack gap="eds.dimension.4">
						<EdsIcon icon="ThumbUp" color="secondary" size="sm" />
						<CosmosText variant={CosmosTextVariantEnum.uiMD} color="eds.text.primary">
							{toShortNum(upVotesCount)}
						</CosmosText>
					</HStack>
					<HStack gap="eds.dimension.4">
						<EdsIcon icon="TalkBubbleDots" color="secondary" size="sm" />
						<CosmosText variant={CosmosTextVariantEnum.uiMD} color="eds.text.primary">
							{toShortNum(threadCommentsCount)}
						</CosmosText>
					</HStack>
				</HStack>
			)}
		</CosmosCard>
	);
}
